/**
 * Live Ops Map building blocks
 *
 * The person-dot and stats shapes the mission-control contract
 * (`display-mission-control.ts`) composes. They were first written for the
 * retired `GET /v1/system/analytics/map` endpoint.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-12-002-feat-admin-live-ops-map-plan.md (U1).
 */

import { z } from 'zod'

/**
 * Resolved, authoritative role for a person dot. Sourced from our DB
 * (`UsersOnSchools` role flags), collapsed to a single value via the
 * multi-role color priority (`admin > faculty > staff > parent > student`),
 * falling back to `uncategorized` when no DB role resolves.
 */
export const MapPersonRoleSchema = z.enum(['parent', 'student', 'faculty', 'staff', 'admin', 'uncategorized'])

export type MapPersonRole = z.infer<typeof MapPersonRoleSchema>

/**
 * One active person, placed by event-level geoip (jitter is applied
 * client-side). `lat`/`lng` may be null when PostHog had no geoip on the
 * event — the dot is still returned so consumers can decide how to place it.
 */
export const MapPersonDotSchema = z.object({
  personId: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  role: MapPersonRoleSchema,
  schoolId: z.string().nullable(),
  schoolName: z.string().nullable(),
  lastActivityAt: z.string()
})

export type MapPersonDot = z.infer<typeof MapPersonDotSchema>

/**
 * Per-role active counts for the stats panel. Sums to `activeUserCount`.
 */
export const MapStatsByRoleSchema = z.object({
  parent: z.number(),
  student: z.number(),
  faculty: z.number(),
  staff: z.number(),
  admin: z.number(),
  uncategorized: z.number()
})

export type MapStatsByRole = z.infer<typeof MapStatsByRoleSchema>

/**
 * At-a-glance ops stats for the overlay panel.
 */
export const MapStatsSchema = z.object({
  activeUserCount: z.number(),
  schoolsLiveCount: z.number(),
  byRole: MapStatsByRoleSchema
})

export type MapStats = z.infer<typeof MapStatsSchema>
